<script setup lang="ts">
import { computed } from 'vue';
import UpgradePremium from '~/pages/upgrade-premium.vue';
const { hasPremiumAccess } = useAuth();

interface GanttCell {
  pumpId: number | null;
  recipeId: number | null;
  pumpName: string | null;
  recipeName: string | null;
  color: string;
  count: number;
}

interface GanttRow {
  holeNo: number;
  cells: (GanttCell | null)[];
}

interface GanttData {
  headers: number[];
  rows: GanttRow[];
}

const props = defineProps<{
  pumprecipeGanttData: GanttData
}>()

const formatTime = (date: number) => {
  if (!date) return ''
  return new Date(date).toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const filteredRows = computed(() => {
  if (!props.pumprecipeGanttData || !props.pumprecipeGanttData.rows) return []
  return props.pumprecipeGanttData.rows
})

const uniquePumps = computed(() => {
  const map = new Map()
  filteredRows.value.forEach(row => {
    row.cells?.forEach(cell => {
      if (cell && !map.has(cell.pumpId)) {
        map.set(cell.pumpId, { id: cell.pumpId, name: cell.pumpName, color: cell.color })
      }
    })
  })
  return Array.from(map.values()).sort((a, b) => a.id - b.id)
})

const uniqueRecipes = computed(() => {
  const map = new Map()
  filteredRows.value.forEach(row => {
    row.cells?.forEach(cell => {
      if (cell && !map.has(cell.recipeId)) {
        map.set(cell.recipeId, { id: cell.recipeId, name: cell.recipeName })
      }
    })
  })
  return Array.from(map.values()).sort((a, b) => a.id - b.id)
})
</script>

<template>
  <UpgradePremium v-if="!hasPremiumAccess" style="min-height: 400px;" />
  <v-container fluid v-else>
    <div class="d-flex justify-end mb-4"></div>

    <!-- Legend -->
    <v-row v-if="uniquePumps.length > 0" class="mb-6">
      <v-col cols="12" md="6">
        <v-card elevation="2" height="100%">
          <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
            Pump Reference
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-row dense>
              <v-col v-for="pump in uniquePumps" :key="pump.id" cols="12" sm="6">
                <div class="d-flex align-center pa-2 border rounded bg-grey-lighten-5">
                  <div
                    class="d-flex align-center justify-center text-white font-weight-bold rounded mr-3 flex-shrink-0"
                    :style="{ backgroundColor: pump.color, width: '24px', height: '24px', fontSize: '0.75rem' }"
                  >
                    {{ pump.id }}
                  </div>
                  <span class="text-body-2 font-weight-medium text-medium-emphasis">{{ pump.name }}</span>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card elevation="2" height="100%">
          <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
            Recipe Reference
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-3">
            <div
              v-for="recipe in uniqueRecipes"
              :key="recipe.id"
              class="d-flex align-center pa-2 mb-1 rounded bg-grey-lighten-5"
            >
              <v-chip
                size="small"
                color="blue-grey"
                variant="tonal"
                class="mr-3 font-weight-bold flex-shrink-0"
                style="min-width: 36px; justify-content: center;"
              >
                R{{ recipe.id }}
              </v-chip>
              <span class="text-body-2 text-medium-emphasis">{{ recipe.name }}</span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-alert
      v-if="!pumprecipeGanttData || !pumprecipeGanttData.rows || pumprecipeGanttData.rows.length === 0"
      type="info"
      variant="tonal"
      class="mb-4"
    >
      No timeline data available.
    </v-alert>

    <v-card v-else elevation="2">
      <v-table density="compact" fixed-header height="70vh" class="gantt-table">
        <thead>
          <tr>
            <th class="sticky-col bg-white text-center font-weight-bold border-e" style="z-index: 30 !important;">Hole No</th>
            <th v-for="(time, index) in pumprecipeGanttData.headers" :key="index" class="text-center text-caption text-medium-emphasis border-b" style="min-width: 48px;">
              {{ formatTime(time) }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in filteredRows" :key="row.holeNo">
            <td class="sticky-col bg-white text-center font-weight-bold border-e font-monospace">{{ row.holeNo }}</td>
            <td v-for="(cell, index) in row.cells" :key="index" class="pa-0 border-b border-e" style="height: 36px; min-width: 48px;">
              <div
                v-if="cell"
                class="d-flex flex-column align-center justify-center cursor-help transition-swing elevation-1"
                :style="{ backgroundColor: cell.color, height: '100%', width: '100%' }"
                :title="`Pump: ${cell.pumpName} | Recipe: ${cell.recipeName} (Used ${cell.count} times)`"
              >
                <span class="text-white font-weight-bold" style="font-size: 0.7rem; line-height: 1.1;">{{ cell.pumpId }}</span>
                <span class="text-white" style="font-size: 0.6rem; line-height: 1.1; opacity: 0.85;">R{{ cell.recipeId }}</span>
              </div>
            </td>
          </tr>
        </tbody>
      </v-table>
      <v-card-text class="text-caption text-medium-emphasis bg-grey-lighten-5 border-t">
        <v-icon size="small" class="mr-1">mdi-information-outline</v-icon>
        Each cell shows the dominant Pump ID (top) and Recipe ID (bottom) within each 15-minute interval.
      </v-card-text>
    </v-card>
  </v-container>
</template>

<style scoped>
.font-monospace {
  font-family: 'Courier New', Courier, monospace !important;
}
.sticky-col {
  position: sticky;
  left: 0;
  z-index: 20;
}
.transition-swing {
  transition: transform 0.1s cubic-bezier(0.25, 0.8, 0.5, 1);
}
.transition-swing:hover {
  transform: scale(1.1);
  z-index: 5;
}
</style>
